import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Heading, 
  Text, 
  Button, 
  VStack, 
  Input, 
  useToast,
  useColorModeValue,
  Radio,
  RadioGroup,
  Stack,
  HStack,
  Link,
  Progress,
  Badge,
  Tooltip
} from '@chakra-ui/react';
import { useGame } from '../../context/GameContext';
import { useWordTracker } from '../../context/WordTrackerContext';
import axios from 'axios';

// All requests go through Vercel's API routes
const API_BASE_URL = '/api';

const StartScreen = () => {
  const { startGame, loading } = useGame();
  const { getNextWord, usedWords } = useWordTracker();
  const [selectedCategory, setSelectedCategory] = useState('');
  const [customWord, setCustomWord] = useState('');
  const [wordLists, setWordLists] = useState({});
  const [lastUpdated, setLastUpdated] = useState('');
  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Fetch word lists on component mount
  useEffect(() => {
    const fetchWordLists = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/word-lists`);
        setWordLists(response.data.categories);
        setLastUpdated(response.data.lastUpdated);
      } catch (error) {
        console.error('Error fetching word lists:', error);
        toast({
          title: 'Error',
          description: 'Failed to load word lists. Please try again.',
          status: 'error',
          duration: 5000,
        });
      }
    };

    fetchWordLists();
  }, [toast]);

  // Calculate progress for each category
  const getCategoryProgress = (category) => {
    const totalWords = wordLists[category]?.length || 0;
    const usedWordsCount = usedWords[category]?.length || 0;
    const percentage = totalWords > 0 ? (usedWordsCount / totalWords) * 100 : 0;
    return {
      used: usedWordsCount,
      total: totalWords,
      percentage
    };
  };

  // Function to format relative time
  const getRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);
    const remainingHours = diffInHours % 24;

    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    } else {
      const daysText = `${diffInDays} day${diffInDays !== 1 ? 's' : ''}`;
      const hoursText = remainingHours > 0 ? ` and ${remainingHours} hour${remainingHours !== 1 ? 's' : ''}` : '';
      return `${daysText}${hoursText} ago`;
    }
  };

  // Handler for starting the game
  const handleStartGame = async () => {
    if (loading) return;

    if (customWord.trim()) {
      // Using a custom word - always show loading as it needs to generate
      toast({
        title: 'Starting game...',
        description: 'Generating your image, please wait',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      
      await startGame(selectedCategory || 'custom', customWord);
    } else {
      // Using a word from the selected category
      if (!selectedCategory) {
        toast({
          title: 'Select a category',
          description: 'Please select a category to play',
          status: 'warning',
          duration: 2000,
        });
        return;
      }
      
      // Get next word from the selected category using the word tracker
      const words = wordLists[selectedCategory] || [];
      const nextWord = getNextWord(selectedCategory, words);
      
      // Start the game without showing loading toast
      // The loading state will be handled by the game component if needed
      await startGame(selectedCategory, nextWord);
    }
  };

  return (
    <Box
      maxW="container.md"
      mx="auto"
      p={8}
      bg={bgColor}
      borderRadius="lg"
      boxShadow="xl"
      borderWidth="1px"
      borderColor={borderColor}
    >
      <VStack spacing={6} align="center">
        <Heading size="xl" textAlign="center">
          Looking to win?
        </Heading>
        
        <Text fontSize="lg" textAlign="center">
          Step up and VisualEyes! Select a category to begin.
        </Text>
        
        {lastUpdated && (
          <Tooltip 
            label={new Date(lastUpdated).toLocaleString(undefined, {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
              timeZoneName: 'short'
            })}
            placement="top"
          >
            <Text fontSize="sm" color="gray.500" textAlign="center" cursor="help">
              New words added {getRelativeTime(lastUpdated)}
            </Text>
          </Tooltip>
        )}
        
        <Box w="100%" maxW="400px">
          <Text mb={2} fontWeight="medium">Select a category:</Text>
          <RadioGroup onChange={setSelectedCategory} value={selectedCategory}>
            <Stack direction="column" spacing={4}>
              {Object.entries(wordLists).map(([category, words]) => {
                const progress = getCategoryProgress(category);
                return (
                  <Box key={category} p={3} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                    <HStack justify="space-between" mb={2}>
                      <Radio value={category}>
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </Radio>
                      <Badge colorScheme={progress.percentage === 100 ? 'green' : 'blue'}>
                        {progress.used}/{progress.total} words guessed
                      </Badge>
                    </HStack>
                    <Progress 
                      value={progress.percentage} 
                      size="sm" 
                      colorScheme={progress.percentage === 100 ? 'green' : 'blue'}
                      borderRadius="full"
                    />
                  </Box>
                );
              })}
            </Stack>
          </RadioGroup>
        </Box>

        <Box w="100%" maxW="400px">
          <Text mb={2} fontWeight="medium">Or, Enter a custom word:</Text>
          <Input
            placeholder="Enter a custom word"
            value={customWord}
            onChange={(e) => setCustomWord(e.target.value)}
            size="lg"
            bg={useColorModeValue('white', 'gray.700')}
            borderColor={borderColor}
            _hover={{ borderColor: useColorModeValue('gray.300', 'gray.500') }}
            _focus={{
              borderColor: useColorModeValue('blue.500', 'blue.300'),
              boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
            }}
          />
        </Box>
        
        <HStack spacing={4} w="100%" justify="center">
          <Button
            colorScheme="blue"
            size="lg"
            onClick={handleStartGame}
            isDisabled={loading}
            isLoading={loading}
            loadingText="Starting game..."
          >
            Start Game
          </Button>
          {loading && (
            <Link
              href="https://buymeacoffee.com/themattrix"
              isExternal
              _hover={{ textDecoration: 'none' }}
            >
              <Button
                size="lg"
                leftIcon={<span role="img" aria-label="coffee">☕</span>}
                variant="outline"
              >
                Coffee Me!
              </Button>
            </Link>
          )}
        </HStack>
      </VStack>
    </Box>
  );
};

export default StartScreen;